import { Card, CardContent } from "@/components/ui/card";
import { AlertCircle, AlertTriangle, CheckCircle2, CalendarClock, Clock } from "lucide-react";
const THAI_SHORT_MONTHS = ["มค", "กพ", "มีค", "เมย", "พค", "มิย", "กค", "สค", "กย", "ตค", "พย", "ธค"];

function formatThaiShort(dateStr: string): string {
  const d = new Date(dateStr);
  const day = d.getDate();
  const month = THAI_SHORT_MONTHS[d.getMonth()];
  const year = (d.getFullYear() + 543) % 100;
  return `${day} ${month} ${year}`;
}

interface DepletionSummary {
  total_depleted: number;
  total_nearly_depleted: number;
  total_renewed: number;
  total_estimated_depleted: number;
  total_estimated_nearly_depleted: number;
}

interface DepletionSummaryCardsProps {
  summary: DepletionSummary | undefined;
  isLoading: boolean;
  startDate: string;
  endDate: string;
}

export function DepletionSummaryCards({ summary, isLoading, startDate, endDate }: DepletionSummaryCardsProps) {
  const today = formatThaiShort(new Date().toISOString());
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-20 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-20 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const topCards = [
    {
      title: "หมดคอร์ส",
      value: summary?.total_depleted ?? 0,
      icon: AlertCircle,
      iconColor: "text-destructive",
      bgColor: "bg-destructive/10",
      description: `ชั่วโมงเป็นศูนย์แล้วในช่วงวันที่ ${formatThaiShort(startDate)} - ${today}`,
    },
    {
      title: "ใกล้หมดคอร์ส",
      value: summary?.total_nearly_depleted ?? 0,
      icon: AlertTriangle,
      iconColor: "text-yellow-600",
      bgColor: "bg-yellow-50 dark:bg-yellow-950",
      description: `ขณะนี้เหลือน้อยกว่า 8 ชม. แต่มากกว่า 0 ช่วงวันที่ ${formatThaiShort(startDate)} - ${today}`,
    },
    {
      title: "เติมชั่วโมงแล้ว",
      value: summary?.total_renewed ?? 0,
      icon: CheckCircle2,
      iconColor: "text-green-600",
      bgColor: "bg-green-50 dark:bg-green-950",
      description: "มีการเติมชม.หลังจากหมด/ใกล้หมด",
    },
  ];

  const bottomCards = [
    {
      title: "คาดว่าจะหมดคอร์ส",
      value: summary?.total_estimated_depleted ?? 0,
      icon: CalendarClock,
      iconColor: "text-purple-600",
      bgColor: "bg-purple-50 dark:bg-purple-950",
      description: `หมดคอร์ส และกำลังจะหมดคอร์สในช่วงวันที่ ${formatThaiShort(startDate)} - ${formatThaiShort(endDate)}`,
    },
    {
      title: "คาดว่าจะใกล้หมด",
      value: summary?.total_estimated_nearly_depleted ?? 0,
      icon: Clock,
      iconColor: "text-blue-600",
      bgColor: "bg-blue-50 dark:bg-blue-950",
      description: `เหลือน้อยกว่า 8 ชม. หรือกำลังจะเหลือน้อยกว่า 8 ชม.ในช่วงวันที่ ${formatThaiShort(startDate)} - ${formatThaiShort(endDate)}`,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {topCards.map((card) => (
          <Card key={card.title} className={card.bgColor}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{card.title}</p>
                  <p className="text-3xl font-bold mt-1">{card.value} คน</p>
                  <p className="text-xs text-muted-foreground mt-1">{card.description}</p>
                </div>
                <card.icon className={`h-8 w-8 ${card.iconColor}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {bottomCards.map((card) => (
          <Card key={card.title} className={card.bgColor}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{card.title}</p>
                  <p className="text-3xl font-bold mt-1">{card.value} คน</p>
                  <p className="text-xs text-muted-foreground mt-1">{card.description}</p>
                </div>
                <card.icon className={`h-8 w-8 ${card.iconColor}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}